import { useDispatchLocationAlarm } from '@hakimo-ui/hakimo/data-access';
import {
  EvidenceUploaderOption,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import { toast } from '@hakimo-ui/hakimo/util';
import { Button } from '@hakimo-ui/shared/ui-base';
import { useRef, useState } from 'react';
import { useAlarmGroupStatic } from '../../unified-alarm-group/alarm-provider/AlarmGroupProvider';
import { EditableTimestampHandle } from '../../shared/EditableTimestamp';
import { useFileHandler, usePasteImages, type ResponderType } from '../util';
import { CommentBox } from './CommentBox';
import { PicturePicker } from './PicturePicker';
import { ResponderToggle } from './ResponderToggle';
import { TimestampField } from './TimestampField';
import { badge, buildDispatchPayload, renderFields } from './util';

export function ArrivalFields({
  locationAlarmId,
  product,
  timezone,
  onUploadSuccess,
  dataVersion: dataVersionProp,
}: EvidenceUploaderOption) {
  const [responder, setResponder] = useState<ResponderType>('POLICE');
  const [responderTouched, setResponderTouched] = useState(false);
  const [notes, setNotes] = useState('');
  const [previewIdx, setPreviewIdx] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const timestampRef = useRef<EditableTimestampHandle>(null);
  const [tsTouched, setTsTouched] = useState(false);
  const { fileList, previewList, onFilesSelect, onFileRemove, setFiles } =
    useFileHandler({ multiple: true });

  usePasteImages(setFiles);

  const { dataVersion: contextDataVersion } = useAlarmGroupStatic();
  const dataVersion = dataVersionProp ?? contextDataVersion;
  const { mutate, isLoading } = useDispatchLocationAlarm(locationAlarmId, {
    product,
    dataVersion:
      product === UnifiedMonitoringProduct.SCAN ? undefined : dataVersion,
    onSuccess: () => {
      toast('Successfully logged!', { type: 'success' });
      onUploadSuccess?.();
    },
    onError: () => toast('Failed to upload!', { type: 'error' }),
  });

  return (
    <div className="flex flex-col gap-2.5">
      {renderFields([
        (n) => (
          <ResponderToggle
            badge={badge(n, responderTouched)}
            value={responder}
            onChange={(v) => {
              setResponder(v);
              setResponderTouched(true);
            }}
          />
        ),
        (n) => (
          <PicturePicker
            badge={badge(n, previewList.length > 0)}
            previewList={previewList}
            fileInputRef={fileInputRef}
            previewIdx={previewIdx}
            setPreviewIdx={setPreviewIdx}
            onFilesSelect={onFilesSelect}
            onFileRemove={onFileRemove}
          />
        ),
        (n) => (
          <TimestampField
            badge={badge(n, tsTouched)}
            timezone={timezone}
            timestampRef={timestampRef}
            onTouched={() => setTsTouched(true)}
          />
        ),
        (n) => (
          <CommentBox
            badge={badge(n, notes.trim().length > 0)}
            value={notes}
            onChange={setNotes}
          />
        ),
      ])}
      <Button
        variant="primary"
        block
        onClick={() =>
          mutate(
            buildDispatchPayload(
              'arrival',
              locationAlarmId,
              responder,
              notes,
              timestampRef.current?.getTimestamp() ?? Date.now(),
              undefined,
              fileList
            )
          )
        }
        loading={isLoading}
        disabled={isLoading}
      >
        Submit
      </Button>
    </div>
  );
}
